import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, effect, inject, signal } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { forkJoin } from 'rxjs';
import { ModalService } from '@parashift/shared/components';
import { HeaderActionStore } from '@parashift/shared/stores';
import { DashboardTemplateApiService, DashboardWidgetApiService, JsonApiError } from '@parashift/shared/services';
import { DashboardTemplate, DashboardWidget, ListFilter } from '@parashift/shared/models';
import { WidgetEditorInput, WidgetEditorModalComponent, WidgetEditorResult } from './widget-editor-modal/widget-editor-modal.component';
import { TemplateEditorInput, TemplateEditorModalComponent, TemplateEditorResult } from './template-editor-modal/template-editor-modal.component';

type Banner = { type: 'success' | 'error'; text: string };

// Route-gated to roles: ['admin']; all writes are re-checked by the id-api policies.
@Component({
  selector: 'pdc-dashboard-admin',
  templateUrl: './dashboard-admin.component.html',
  styleUrls: ['./dashboard-admin.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FaIconComponent]
})
export class DashboardAdminComponent implements OnInit {
  private templateApi = inject(DashboardTemplateApiService);
  private widgetApi = inject(DashboardWidgetApiService);
  private modalService = inject(ModalService);
  private headerActionStore = inject(HeaderActionStore);

  readonly templates = signal<DashboardTemplate[]>([]);
  readonly selectedId = signal<string | null>(null);
  readonly loading = signal(false);
  readonly banner = signal<Banner | null>(null);

  // Localized literals used inside template interpolations (which can't carry an i18n attribute).
  protected readonly onLabel = $localize`:@@pdc.dashboard_admin.badge.on:on`;
  protected readonly offLabel = $localize`:@@pdc.dashboard_admin.badge.off:off`;

  readonly selected = computed(() => this.templates().find(template => template.id === this.selectedId()) ?? null);
  readonly widgets = computed(() => {
    const template = this.selected();
    return template ? [...(template.dashboard_widgets ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)) : [];
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.headerActionStore.destroy());

    this.headerActionStore.addActions({ loadingButton: { dispatch: () => this.reload() } });
    effect(() => this.headerActionStore.updateActions({ loadingButton: { dispatch: () => this.reload(), disabled: this.loading() } }));
  }

  ngOnInit(): void {
    this.reload();
  }

  // ---- loading -------------------------------------------------------------

  reload(selectId?: string | null): void {
    this.loading.set(true);
    this.templateApi.findAll(new ListFilter({ include: 'dashboard_widgets' })).subscribe({
      next: (result) => {
        const loaded = result.getModels().sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        this.templates.set(loaded);
        this.selectedId.set(selectId ?? this.selectedId() ?? loaded[0]?.id ?? null);
        this.loading.set(false);
      },
      error: (error) => this.fail($localize`:@@pdc.dashboard_admin.msg.load_failed:Failed to load dashboards`, error)
    });
  }

  selectTemplate(id: string): void {
    this.selectedId.set(id);
  }

  protected gridCols(grid: Record<string, unknown> | undefined): number {
    return Number(grid?.['cols']) || 6;
  }

  // ---- widgets -------------------------------------------------------------

  openWidgetEditor(widget?: DashboardWidget): void {
    const template = this.selected();
    if (!template) {
      return;
    }
    const input: WidgetEditorInput = { editingWidget: widget ?? null, nextPosition: this.widgets().length };
    this.modalService.customized({
      initialState: {
        model: input,
        class: 'modal-lg modal-dialog-centered modal-dialog-scrollable'
      }
    }, WidgetEditorModalComponent).subscribe((result: WidgetEditorResult | false) => {
      if (result) {
        this.persistWidget(template.id, result);
      }
    });
  }

  private persistWidget(templateId: string, result: WidgetEditorResult): void {
    const widget = new DashboardWidget({
      dashboard_template_id: templateId,
      title: result.title,
      widget_type: result.widgetType,
      icon: result.icon || null,
      grid: { cols: result.cols },
      position: result.position,
      required_roles: result.requiredRoles,
      settings: result.settings
    });
    if (result.id) {
      widget.id = result.id;
    }

    this.loading.set(true);
    this.widgetApi.saveRecord(widget).subscribe({
      next: () => {
        this.succeed(result.id
          ? $localize`:@@pdc.dashboard_admin.msg.widget_updated:Widget updated.`
          : $localize`:@@pdc.dashboard_admin.msg.widget_added:Widget added.`);
        this.reload(templateId);
      },
      error: (error) => this.fail($localize`:@@pdc.dashboard_admin.msg.widget_save_failed:Saving the widget failed`, error)
    });
  }

  deleteWidget(widget: DashboardWidget): void {
    this.modalService.confirmation({
      initialState: {
        title: $localize`:@@pdc.dashboard_admin.confirm.delete_widget_title:Delete widget?`,
        description: $localize`:@@pdc.dashboard_admin.confirm.delete_widget:Delete widget "${widget.title}:title:"?`,
        button_confirm: $localize`:@@pdc.dashboard_admin.confirm.delete_widget_action:Delete`,
        class: 'modal-sm'
      }
    }).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      const templateId = this.selected()?.id;
      this.loading.set(true);
      this.widgetApi.deleteRecord(widget.id).subscribe({
        next: () => {
          this.succeed($localize`:@@pdc.dashboard_admin.msg.widget_deleted:Widget deleted.`);
          this.reload(templateId);
        },
        error: (error) => this.fail($localize`:@@pdc.dashboard_admin.msg.widget_delete_failed:Deleting the widget failed`, error)
      });
    });
  }

  moveWidget(widget: DashboardWidget, direction: -1 | 1): void {
    const list = this.widgets();
    const index = list.indexOf(widget);
    const target = index + direction;
    if (target < 0 || target >= list.length) {
      return;
    }
    // Swap by display index, not stored position — two widgets can share the same position,
    // which would make swapping their own values a no-op.
    const neighbour = list[target];
    widget.position = target;
    neighbour.position = index;

    this.loading.set(true);
    forkJoin([this.widgetApi.saveRecord(widget), this.widgetApi.saveRecord(neighbour)]).subscribe({
      next: () => {
        this.succeed($localize`:@@pdc.dashboard_admin.msg.reordered:Reordered.`);
        this.reload(this.selected()?.id);
      },
      error: (error) => this.fail($localize`:@@pdc.dashboard_admin.msg.reorder_failed:Reordering failed`, error)
    });
  }

  // ---- templates -----------------------------------------------------------

  openTemplateEditor(template?: DashboardTemplate): void {
    const input: TemplateEditorInput = { editingTemplate: template ?? null, nextPosition: this.templates().length };
    this.modalService.customized({
      initialState: {
        model: input,
        class: 'modal-md modal-dialog-centered'
      }
    }, TemplateEditorModalComponent).subscribe((result: TemplateEditorResult | false) => {
      if (result) {
        this.persistTemplate(result);
      }
    });
  }

  private persistTemplate(result: TemplateEditorResult): void {
    const template = new DashboardTemplate({
      key: result.key,
      name: result.name,
      visibility: result.visibility,
      enabled: result.enabled,
      position: result.position
    });
    if (result.id) {
      template.id = result.id;
    }

    this.loading.set(true);
    this.templateApi.saveRecord(template).subscribe({
      next: (saved) => {
        this.succeed(result.id
          ? $localize`:@@pdc.dashboard_admin.msg.template_updated:Template updated.`
          : $localize`:@@pdc.dashboard_admin.msg.template_created:Template created.`);
        this.reload(saved.id);
      },
      error: (error) => this.fail($localize`:@@pdc.dashboard_admin.msg.template_save_failed:Saving the template failed`, error)
    });
  }

  deleteTemplate(template: DashboardTemplate): void {
    this.modalService.confirmation({
      initialState: {
        title: $localize`:@@pdc.dashboard_admin.confirm.delete_template_title:Delete template?`,
        description: $localize`:@@pdc.dashboard_admin.confirm.delete_template:Delete template "${template.name}:name:" and all its widgets?`,
        button_confirm: $localize`:@@pdc.dashboard_admin.confirm.delete_template_action:Delete`,
        class: 'modal-sm'
      }
    }).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.loading.set(true);
      this.templateApi.deleteRecord(template.id).subscribe({
        next: () => {
          this.succeed($localize`:@@pdc.dashboard_admin.msg.template_deleted:Template deleted.`);
          this.selectedId.set(null);
          this.reload();
        },
        error: (error) => this.fail($localize`:@@pdc.dashboard_admin.msg.template_delete_failed:Deleting the template failed`, error)
      });
    });
  }

  // ---- helpers -------------------------------------------------------------

  private succeed(text: string): void {
    this.loading.set(false);
    this.banner.set({ type: 'success', text });
  }

  private fail(text: string, error: unknown): void {
    this.loading.set(false);
    const reason = this.reason(error);
    this.banner.set({ type: 'error', text: reason ? `${text}: ${reason}` : `${text}.` });
  }

  // A JSON:API error has `errors` at the top level; a raw HttpErrorResponse has it under `error`.
  private reason(error: unknown): string {
    const source = error as { errors?: JsonApiError[]; error?: { errors?: JsonApiError[] } };
    return (source?.errors ?? source?.error?.errors ?? [])
      .map(item => item?.detail || item?.title)
      .filter(Boolean)
      .join('; ');
  }
}
